import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';

/**
 * Seleção com busca por digitação, para listas longas.
 *
 * Um `<select>` nativo com 305 extensões obriga a rolar procurando o nome —
 * foi o que a secretaria reclamou. Aqui filtra-se digitando, e a lista é
 * pequena o bastante para o filtro acontecer no cliente, sem ida ao servidor
 * a cada tecla.
 *
 * Mantém um `<input type="hidden">` com o `name` recebido para o campo
 * continuar tendo a mesma semântica de formulário do `<select>` que substitui.
 */
const ComboBoxBusca = ({
  id,
  name,
  value,
  onChange,
  opcoes = [],
  placeholder = 'Selecione...',
  placeholderBusca = 'Digite para buscar...',
  textoVazio = 'Nenhuma opção encontrada',
  erro = false,
  disabled = false,
  className = '',
}) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [indiceAtivo, setIndiceAtivo] = useState(0);

  const containerRef = useRef(null);
  const buscaRef = useRef(null);
  const listaRef = useRef(null);

  const selecionada = opcoes.find((o) => String(o.valor) === String(value));

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return opcoes;
    return opcoes.filter((o) => o.rotulo.toLowerCase().includes(termo));
  }, [opcoes, busca]);

  useEffect(() => {
    const aoClicarFora = (evento) => {
      if (containerRef.current && !containerRef.current.contains(evento.target)) {
        setAberto(false);
        setBusca('');
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, []);

  useEffect(() => {
    if (aberto && buscaRef.current) buscaRef.current.focus();
  }, [aberto]);

  useEffect(() => {
    setIndiceAtivo(0);
  }, [busca]);

  // Mantém a opção destacada dentro da área visível ao navegar pelo teclado.
  useEffect(() => {
    const lista = listaRef.current;
    if (!lista) return;
    const item = lista.children[indiceAtivo];
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [indiceAtivo]);

  const escolher = (opcao) => {
    onChange(opcao ? opcao.valor : '');
    setAberto(false);
    setBusca('');
  };

  const aoTeclar = (evento) => {
    if (!aberto && ['ArrowDown', 'Enter', ' '].includes(evento.key)) {
      evento.preventDefault();
      setAberto(true);
      return;
    }

    if (!aberto) return;

    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setIndiceAtivo((i) => Math.min(i + 1, filtradas.length - 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setIndiceAtivo((i) => Math.max(i - 1, 0));
    } else if (evento.key === 'Enter') {
      evento.preventDefault();
      if (filtradas[indiceAtivo]) escolher(filtradas[indiceAtivo]);
    } else if (evento.key === 'Escape') {
      evento.preventDefault();
      setAberto(false);
      setBusca('');
    }
  };

  const borda = erro ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-green-400';

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setAberto((a) => !a)}
        onKeyDown={aoTeclar}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        className={`w-full min-h-11 px-3 py-2 rounded border text-left flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${borda}`}
      >
        <span className={`truncate ${selecionada ? 'text-gray-900' : 'text-gray-500'}`}>
          {selecionada ? selecionada.rotulo : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selecionada && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Limpar seleção"
              onClick={(e) => { e.stopPropagation(); escolher(null); }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon={faTimes} />
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`text-gray-400 transition-transform duration-200 ${aberto ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {aberto && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                ref={buscaRef}
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={aoTeclar}
                placeholder={placeholderBusca}
                aria-label={placeholderBusca}
                className="w-full min-h-11 pl-9 pr-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>

          <ul ref={listaRef} role="listbox" className="max-h-60 overflow-y-auto">
            {filtradas.length === 0 && (
              <li className="px-3 py-3 text-sm text-gray-500">{textoVazio}</li>
            )}
            {filtradas.map((opcao, indice) => (
              <li key={opcao.valor} role="option" aria-selected={String(opcao.valor) === String(value)}>
                <button
                  type="button"
                  onMouseEnter={() => setIndiceAtivo(indice)}
                  onClick={() => escolher(opcao)}
                  className={`w-full min-h-11 px-3 py-2 text-left text-sm focus:outline-none ${
                    indice === indiceAtivo ? 'bg-green-50' : ''
                  } ${String(opcao.valor) === String(value) ? 'font-semibold text-green-700' : 'text-gray-800'}`}
                >
                  {opcao.rotulo}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <input type="hidden" name={name} value={value ?? ''} />
    </div>
  );
};

export default ComboBoxBusca;
